import { useEffect, useState } from 'react'
import { Wrapper } from '@farewill/ui'
import { DocumentForm } from './document-form'
import { arrayMoveImmutable } from 'array-move'
import type {
  DocumentWithAttributes,
  File,
  Document,
} from 'lib/models/document'
import type { PreviewFile, DocumentFormValues } from './types'
import { useDispatch } from 'react-redux'
import { hideModal } from 'state/actions'

export const EditModal = ({
  config: { document, updateFile, updateDocument },
}: {
  config: {
    document: DocumentWithAttributes
    updateFile: (id: number, file: Partial<File>) => Promise<void>
    updateDocument: (
      id: number,
      document: Partial<Document>
    ) => Promise<DocumentWithAttributes>
  }
}) => {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [files, setFiles] = useState<File[]>([])

  const filesForPreview = files.map((file, index): PreviewFile => {
    return {
      name: `File ${index + 1}`,
      preview: file.url,
      type: file.type,
    }
  })

  const handleSubmit = async (values: DocumentFormValues) => {
    try {
      setIsSubmitting(true)
      await Promise.all(
        files.map((file, orderIndex) => updateFile(file.id, { orderIndex }))
      )
      await updateDocument(document.id, {
        title: values.title,
        category: values.category,
      })
      dispatch(hideModal())
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileOrderChanged = (oldIndex: number, newIndex: number) => {
    setFiles(arrayMoveImmutable(files, oldIndex, newIndex))
  }

  useEffect(() => {
    setFiles(document.attributes.files)
  }, [document])

  return (
    <Wrapper data-testid="edit-document-modal">
      <DocumentForm
        title={document.attributes.title}
        category={document.attributes.category}
        url={document.attributes.files[0]?.url || ''}
        files={filesForPreview}
        onFileOrderChanged={handleFileOrderChanged}
        onCancel={() => dispatch(hideModal())}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isEditMode
      />
    </Wrapper>
  )
}
